<template>
  <b-nav-item-dropdown
    right
    toggle-class="d-flex align-items-center dropdown-user-link poda-profile-toggle"
    class="dropdown-user poda-user-dropdown"
    menu-class="poda-user-dropdown-menu shadow"
  >
    <template #button-content>
      <b-avatar
        size="40"
        :src="userData ? userData.image : ''"
        :text="avatarInitials"
        variant="light-primary"
        badge
        class="badge-minimal poda-navbar-avatar"
        badge-variant="success"
      />
    </template>

    <div
      v-if="isSideDataLoading"
      class="d-flex justify-content-center align-items-center py-2"
      style="min-height: 120px"
    >
      <b-spinner label="Loading..." />
    </div>

    <template v-else>
      <b-dropdown-text
        tag="div"
        class="poda-profile-dropdown__header"
      >
        <div class="d-flex align-items-center">
          <div class="poda-profile-dropdown__avatar-wrap mr-75">
            <b-avatar
              size="42"
              :src="userData ? userData.image : ''"
              :text="avatarInitials"
              variant="light-primary"
              badge
              class="badge-minimal"
              badge-variant="success"
            />
          </div>
          <div class="min-w-0 flex-grow-1 text-left">
            <div class="poda-profile-dropdown__name text-truncate">
              {{ displayName }}
            </div>
            <div class="poda-profile-dropdown__role text-truncate">
              {{ userRole }}
            </div>
          </div>
        </div>
      </b-dropdown-text>

      <b-dropdown-divider class="poda-profile-dropdown__divider" />

      <b-dropdown-item
        :to="{ name: 'profile-account-setting' }"
        link-class="poda-profile-dropdown__link d-flex align-items-center"
      >
        <img
          src="/svg/profile/myprofile.svg"
          alt=""
          class="poda-profile-dropdown__icon"
        >
        <span>My Profile</span>
      </b-dropdown-item>
      <b-dropdown-item
        :to="{ name: 'password-account-setting' }"
        link-class="poda-profile-dropdown__link d-flex align-items-center"
      >
        <img
          src="/svg/profile/changepassword.svg"
          alt=""
          class="poda-profile-dropdown__icon"
        >
        <span>Change Password</span>
      </b-dropdown-item>

      <b-dropdown-divider class="poda-profile-dropdown__divider" />

      <b-dropdown-item
        :to="{ name: 'pages-pricing' }"
        link-class="poda-profile-dropdown__link d-flex align-items-center"
      >
        <img
          src="/svg/profile/billingplans.svg"
          alt=""
          class="poda-profile-dropdown__icon"
        >
        <span>Billings &amp; Plans</span>
      </b-dropdown-item>
      <b-dropdown-item
        :to="{ name: 'pages-help' }"
        link-class="poda-profile-dropdown__link d-flex align-items-center"
      >
        <img
          src="/svg/profile/help.svg"
          alt=""
          class="poda-profile-dropdown__icon"
        >
        <span>Help</span>
      </b-dropdown-item>
      <b-dropdown-item
        :to="{ name: 'pages-faq' }"
        link-class="poda-profile-dropdown__link d-flex align-items-center"
      >
        <img
          src="/svg/profile/faq.svg"
          alt=""
          class="poda-profile-dropdown__icon"
        >
        <span>FAQ</span>
      </b-dropdown-item>

      <b-dropdown-divider class="poda-profile-dropdown__divider" />

      <b-dropdown-item
        class="poda-profile-dropdown__signout"
        link-class="poda-profile-dropdown__link d-flex align-items-center"
        @click="logout"
      >
        <img
          src="/svg/profile/signout.svg"
          alt=""
          class="poda-profile-dropdown__icon"
        >
        <span>Sign Out</span>
      </b-dropdown-item>
    </template>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown,
  BDropdownItem,
  BDropdownDivider,
  BDropdownText,
  BAvatar,
  BSpinner,
} from 'bootstrap-vue'
import { avatarText } from '@core/utils/filter'
import axios from '@/component/axios'
import { teardownVendorWebPush } from '@/plugins/webPush'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import { getUserData, resolveUserDisplayName, resolveUserRoleLabel } from '@/auth/utils'

export default {
  components: {
    BNavItemDropdown,
    BDropdownItem,
    BDropdownDivider,
    BDropdownText,
    BAvatar,
    BSpinner,
  },
  data() {
    return {
      accessToken: localStorage.getItem('accessToken'),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      userData: null,
      isSideDataLoading: false,
    }
  },
  computed: {
    displayName() {
      return resolveUserDisplayName(this.userData)
    },
    userRole() {
      return resolveUserRoleLabel(this.userData)
    },
    avatarInitials() {
      const u = this.userData
      if (!u) return ''
      const label = resolveUserDisplayName(u)
      if (label && label !== 'User') return avatarText(label)
      return 'U'
    },
  },
  created() {
    this.syncUserDataFromStorage()
    this.loadVendorProfile()
  },
  methods: {
    syncUserDataFromStorage() {
      this.userData = getUserData()
    },
    async loadVendorProfile() {
      if (!this.accessToken) return
      this.isSideDataLoading = true
      try {
        const res = await vendorPanelApi.profile()
        const body = res?.data || {}
        const data = body.data && typeof body.data === 'object' ? body.data : body
        const merged = {
          ...(this.userData || {}),
          company_name: data.company_name || data.compny_name,
          compny_name: data.company_name || data.compny_name,
          contact_person_name: data.contact_person_name,
          email: data.email || this.userData?.email,
          image: data.image || this.userData?.image,
        }
        if (!merged.name && data.contact_person_name) {
          merged.name = data.contact_person_name
        }
        this.userData = merged
        localStorage.setItem('userData', JSON.stringify(merged))
      } catch (e) {
        // keep localStorage userData
      } finally {
        this.isSideDataLoading = false
      }
    },
    async logout() {
      try {
        await axios({
          method: 'POST',
          url: `${this.baseApi}auth/logout`,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.accessToken}`,
          },
        })
      } catch (e) {
        // still clear local session
      } finally {
        teardownVendorWebPush()
        localStorage.clear()
        this.$router.push({ name: 'auth-login' })
      }
    },
  },
}
</script>

<style lang="scss">
/* Menu attaches to body; avoid scoped so classes apply. */
.poda-user-dropdown-menu {
  min-width: 16.5rem;
  max-width: 18rem;
  padding: 0.4rem 0 0.5rem;
  border: 0;
  border-radius: 0.65rem;
  box-shadow: 0 8px 28px rgba(24, 22, 45, 0.12) !important;

  .dropdown-item {
    color: #4a4a5a;
  }

  .poda-profile-dropdown__header {
    padding: 0.9rem 1.1rem 0.75rem;
    margin: 0;
  }

  .poda-profile-dropdown__name {
    font-size: 0.95rem;
    font-weight: 700;
    color: #2d2a3a;
    line-height: 1.25;
  }

  .poda-profile-dropdown__role {
    font-size: 0.8rem;
    color: #8e8a9d;
    margin-top: 0.1rem;
    line-height: 1.2;
  }

  .poda-profile-dropdown__divider {
    margin: 0.2rem 0;
    border-top-color: #ededf2;
  }

  a.poda-profile-dropdown__link,
  button.poda-profile-dropdown__link {
    font-size: 0.9rem;
    font-weight: 500;
    padding: 0.6rem 1.1rem !important;
    color: #3f3d4d !important;

    span {
      color: #3f3d4d;
    }

    &:hover,
    &:focus {
      background: #f8f8fb;
      color: #2d2a3a !important;
    }
  }

  .poda-profile-dropdown__signout a.poda-profile-dropdown__link,
  .poda-profile-dropdown__signout button.poda-profile-dropdown__link {
    color: #3f3d4d !important;
  }

  .poda-profile-dropdown__icon {
    width: 16px;
    height: 16px;
    margin-right: 0.65rem;
    flex-shrink: 0;
    object-fit: contain;
  }
}

.poda-navbar-avatar {
  .b-avatar-text {
    font-weight: 600;
  }
}

.poda-profile-toggle.dropdown-toggle::after {
  display: none;
}
</style>
